import type { Vibe } from "@majourneys/shared";
import type { DiscoverItem } from "../types";

/**
 * What a listing has to be for a trip's vibe to call it a match.
 *
 * Read off the two things every catalogue row carries: its segment (what
 * Discover files it under) and its category slug (what the team filed it
 * under). Bookable rows all arrive as segment "activities", so for them the
 * category is the only signal; explore rows have no category but a precise
 * segment. A vibe matches a row when either list names it. `cities` covers the
 * one vibe that is a place more than a kind of thing: the desert is Agafay.
 */
interface VibeRule {
  readonly segments?: readonly string[];
  readonly categories?: readonly string[];
  readonly cities?: readonly string[];
}

export const VIBE_RULES: Record<Vibe, VibeRule> = {
  adventure: {
    categories: ["quad", "buggy", "air", "water", "jet-ski", "diving", "boats", "horse", "excursions", "day-trips", "camel"],
  },
  "chill-spa": {
    segments: ["spas"],
    categories: ["spas", "pools"],
  },
  foodie: {
    segments: ["restaurants"],
    categories: ["restaurants", "cooking-class"],
  },
  culture: {
    segments: ["locations", "guides"],
    categories: ["city-tour", "crafts", "calligraphy-painting", "pottery", "weaving", "zellige", "day-trips"],
  },
  nightlife: {
    segments: ["nightlife"],
    categories: ["nightclubs", "shows"],
  },
  "beach-water": {
    segments: ["beach-clubs"],
    categories: ["water", "jet-ski", "diving", "boats", "pools"],
  },
  desert: {
    categories: ["camp", "camel", "quad", "buggy"],
    cities: ["agafay"],
  },
  family: {
    categories: ["camel", "horse", "pools", "city-tour", "cooking-class", "crafts", "pottery"],
  },
  romantic: {
    segments: ["spas"],
    categories: ["air", "shows", "camp", "spas"],
  },
};

type Matchable = Pick<DiscoverItem, "segment" | "categorySlug" | "citySlug">;

/** Whether one vibe calls this listing a match. */
export function matchesVibe(item: Matchable, vibe: Vibe): boolean {
  const rule = VIBE_RULES[vibe];
  if (!rule) return false;
  if (rule.segments?.includes(item.segment)) return true;
  if (item.categorySlug && rule.categories?.includes(item.categorySlug)) return true;
  if (item.citySlug && rule.cities?.includes(item.citySlug)) return true;
  return false;
}

/** How many of the trip's vibes this listing answers. 0 is no match. */
export function vibeScore(item: Matchable, vibes: readonly Vibe[]): number {
  let score = 0;
  for (const vibe of vibes) if (matchesVibe(item, vibe)) score += 1;
  return score;
}

/**
 * "For you": the listings that answer the trip's vibes, best first.
 *
 * More vibes answered ranks higher, then editorial mentions, then the order
 * the catalogue gave. A trip with no vibes, or vibes nothing in the list
 * answers, gets the whole list back by mentions, so the chip is never an
 * empty screen.
 */
export function rankForVibes<T extends Matchable & Pick<DiscoverItem, "mentions">>(
  items: readonly T[],
  vibes: readonly Vibe[],
): T[] {
  const indexed = items.map((item, index) => ({ item, index, score: vibeScore(item, vibes) }));
  const matched = indexed.filter((e) => e.score > 0);
  const pool = matched.length > 0 ? matched : indexed;
  pool.sort(
    (a, b) =>
      b.score - a.score || (b.item.mentions ?? 0) - (a.item.mentions ?? 0) || a.index - b.index,
  );
  return pool.map((e) => e.item);
}

/** The three plain filters next to "For you". */
export type StopChip = "eat" | "do" | "relax";

/**
 * Which plain filter a listing sits under, or null for none of them.
 *
 * Eat is restaurants, whether bookable or not. Relax is spas, pools and beach
 * clubs. Stays are none of the three: a bed is not a stop in a day. Everything
 * else is something to do.
 */
export function stopChipOf(item: Pick<DiscoverItem, "segment" | "categorySlug">): StopChip | null {
  if (item.segment === "stays" || item.categorySlug === "camp") return null;
  if (item.segment === "restaurants" || item.categorySlug === "restaurants") return "eat";
  if (
    item.segment === "spas" ||
    item.segment === "beach-clubs" ||
    item.categorySlug === "spas" ||
    item.categorySlug === "pools"
  ) {
    return "relax";
  }
  return "do";
}
